import {Component, OnDestroy, OnInit} from '@angular/core';
import {MainNavItemComponent} from '../main-nav-item/main-nav-item.component';
import {AuthService} from '@core/services/auth.service';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {MenuModule} from 'primeng/menu';
import {MenuItem, PrimeTemplate} from 'primeng/api';
import {ProgressBarModule} from 'primeng/progressbar';
import {LoadingService} from '@core/services/loading.service';
import {filter, Subscription} from 'rxjs';
import {ApplicationRole} from "@core/enumerations/application-role.enum";
import {ApiAuthService} from "@core/services/api/api-auth.service";
import {Event, NavigationStart, Router, RouterEvent} from "@angular/router";
import {RuntimeEnvironmentService} from '@core/services/runtime-environment.service';
import {animate, style, transition, trigger} from '@angular/animations';
import {ReportGroup} from '@core/models/report-group.model';
import {AppStore} from '@core/stores/app-store.service';
import {User} from "@core/models/user.model";
import {MiscMessageService} from '@core/services/misc-message.service';

@Component({
  selector: 'app-main-nav',
  standalone: true,
  imports: [
    CommonModule,
    MainNavItemComponent,
    MenuModule,
    NgOptimizedImage,
    ProgressBarModule,
    PrimeTemplate
  ],
  animations: [
    trigger(
      'inOutAnimation',
      [
        transition(
          ':enter',
          [
            style({opacity: 0}),
            animate('.1s ease-out',
              style({opacity: 1}))
          ]
        ),
        transition(
          ':leave',
          [
            style({opacity: 1}),
            animate('.1s ease-in',
              style({opacity: 0}))
          ]
        )
      ]
    )
  ],
  templateUrl: './main-nav.component.html',
  styleUrl: './main-nav.component.scss'
})
export class MainNavComponent implements OnInit, OnDestroy {
  reportGroups: ReportGroup[] = [];
  currentUser?: User;
  userMenuItems!: MenuItem[];
  adminMenuItems!: MenuItem[];
  reportGroupMenuItems!: MenuItem[][];
  isLoading = false;
  private loadingSubscription!: Subscription;
  private reportGroupsSubscription!: Subscription;
  private activeReportIdSubscription!: Subscription;
  private currentUserSubscription!: Subscription;
  protected readonly cfg: RuntimeEnvironmentService;

  constructor(
    private readonly authService: AuthService,
    private readonly loadingService: LoadingService,
    private readonly apiAuthService: ApiAuthService,
    private readonly router: Router,
    private readonly runtimeEnv: RuntimeEnvironmentService,
    private readonly appStore: AppStore,
    private readonly miscMessageService: MiscMessageService
  ) {
    this.cfg = runtimeEnv;
  }


  ngOnInit() {
    this.currentUserSubscription = this.apiAuthService.currentUser$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
      }
    });
    this.loadingSubscription = this.loadingService.isLoading$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    this.loadingService.show();

    this.activeReportIdSubscription = this.appStore.activeReportId$.subscribe(reportId => {
      if (reportId) {
        this.reportGroups.forEach(reportGroup => {
          reportGroup.isActive = reportGroup.reports?.some(r => r.id === reportId) ?? false;
        });
      }
    });

    this.reportGroupsSubscription = this.appStore.reportGroups$.subscribe(reportGroups => {
      if (reportGroups) {
        this.loadingService.show();

        this.reportGroups = reportGroups;
        this.setReportGroupMenuItems();

        this.loadingService.hide();
      }
    });

    this.router.events.pipe(
      filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationStart)
    ).subscribe(() => {
      this.apiAuthService.setRoles();
    });

    this.appStore.fetchReportGroups();
    this.apiAuthService.getCurrentUser();
    this.setMenuItems();
  }

  isAdmin() {
    const appRole = this.apiAuthService.getStoredRoles();
    if (appRole === null) {
      return false;
    }
    return ApplicationRole.administrator === appRole[0];
  }

  ngOnDestroy() {
    this.loadingSubscription?.unsubscribe();
    this.reportGroupsSubscription?.unsubscribe();
    this.activeReportIdSubscription?.unsubscribe();
    this.currentUserSubscription.unsubscribe();
  }

  private setReportGroupMenuItems() {
    this.reportGroupMenuItems = this.reportGroups.map(reportGroup => reportGroup.reports!.map(report => <MenuItem>{
      label: report.name,
      icon: 'pi pi-file',
      routerLink: ['/dashboard', report.id]
    }));
  }

  private setMenuItems() {
    this.adminMenuItems = [
      {
        label: 'Gebruikers',
        icon: 'pi pi-users',
        routerLink: 'users'
      },
      {
        label: 'Rapporten',
        icon: 'pi pi-sitemap',
        routerLink: 'reports'
      },
      {
        label: 'Rollen',
        icon: 'pi pi-check-circle',
        routerLink: 'roles'
      },
      {
        label: 'Welkomstbericht',
        icon: 'pi pi-envelope',
        routerLink: 'welcome-messages'
      }
    ];

    this.userMenuItems = [
      {
        separator: true
      },
      {
        label: 'Toon alle berichten opnieuw',
        icon: 'pi pi-refresh',
        command: () => this.resetSeenMessages()
      },
      {
        label: 'Log uit',
        icon: 'pi pi-sign-out',
        command: async () => await this.logOut()
      }
    ];
  }

  private resetSeenMessages() {
    this.miscMessageService.resetSeenMessages();
  }

  private async logOut() {
    await this.authService.logout();
  }
}
